import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { cleSections, deplacer, enregistrerSections, ordonner, useDisposition } from "@/lib/disposition";
import { OrdreBoutons } from "@/components/MenuNav";

const cle = (p) => `onglet-section:${p}`;
const slug = (t, i) => `sec-${i}-${t.toLowerCase().normalize("NFD").replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`;
// Libellé court et clé stable : sans les montants ni compteurs qui changent
const court = (t) => t.split(/ · | \(/)[0].trim();
const stable = (t) => court(t).replace(/[\d\s.,$%/]+/g, " ").trim();

const conteneur = (els) => {
  let c = els[0].parentElement;
  while (c && !els.every((e) => c.contains(e))) { c = c.parentElement; }
  return c;
};

// Ordre visuel des blocs (enfants du conteneur commun qui portent des sections) via la propriété CSS order
const disposer = (els, ordre) => {
  const c = conteneur(els);
  const enfants = [...c.children];
  const blocs = enfants.map((b) => ({ b, s: els.filter((e) => b.contains(e)) })).filter((x) => x.s.length)
    .map((x) => ({ ...x, cle: stable(x.s[0].dataset.section) }));
  const tries = ordonner(blocs, ordre, (x) => x.cle);
  const actif = ordre.length > 0;
  if (actif && !c.dataset.disposition && getComputedStyle(c).display === "block") {
    c.dataset.disposition = "1";
    c.style.display = "flex";
    c.style.flexDirection = "column";
  }
  if (!actif && c.dataset.disposition) {
    delete c.dataset.disposition;
    c.style.display = "";
    c.style.flexDirection = "";
  }
  const pos = new Map(tries.map((x, k) => [x.b, (k + 1) * 10]));
  let avant = 0;
  enfants.forEach((b) => {
    if (pos.has(b)) { avant = pos.get(b); }
    const o = actif ? String(pos.get(b) ?? (avant ? avant + 1 : 0)) : "";
    if (b.style.order !== o) { b.style.order = o; }
  });
  return { secs: tries.flatMap((x) => x.s), blocs: tries.map((x) => x.cle), blocDe: Object.fromEntries(tries.flatMap((x) => x.s.map((e) => [e.id, x.cle]))) };
};

// Onglets en haut de chaque page : un onglet par section (cadre titré) de la page ; le dernier choisi est gardé
export const SectionTabs = () => {
  const { pathname } = useLocation();
  const [sections, setSections] = useState([]);
  const [actif, setActif] = useState(null);
  const restaure = useRef(null);
  const clic = useRef(0);
  const nav = useRef(null);
  const disp = useDisposition();
  const ordre = disp?.sections?.[cleSections(pathname)] || [];
  const ordreRef = useRef(ordre);
  const blocsRef = useRef({ blocs: [], blocDe: {} });
  const lireRef = useRef(null);
  const [org, setOrg] = useState(false);
  ordreRef.current = ordre;
  const ordreCle = JSON.stringify(ordre);
  useEffect(() => { lireRef.current?.(); }, [ordreCle]);
  useEffect(() => {
    const el = nav.current;
    if (!el) { return undefined; }
    const maj = () => {
      const tete = [...document.querySelectorAll("header")].find((x) => x.offsetHeight > 0);
      const h = getComputedStyle(el).position === "sticky" ? el.offsetHeight : tete?.offsetHeight || 48;
      document.documentElement.style.setProperty("--onglets-h", `${h}px`);
    };
    maj();
    const ro = new ResizeObserver(maj);
    ro.observe(el);
    window.addEventListener("resize", maj);
    return () => { ro.disconnect(); window.removeEventListener("resize", maj); document.documentElement.style.setProperty("--onglets-h", "48px"); };
  }, [sections.length > 1]);
  useEffect(() => {
    const main = document.getElementById("contenu-principal");
    const lire = () => {
      const els = [...main.querySelectorAll("section[data-section]")].filter((el) => !el.parentElement.closest("section[data-section]"));
      els.forEach((el, i) => { if (!el.id) { el.id = slug(el.dataset.section, i); } });
      const d = els.length > 1 ? disposer(els, ordreRef.current) : { secs: els, blocs: [], blocDe: {} };
      blocsRef.current = d;
      const liste = d.secs.map((el) => ({ id: el.id, titre: el.dataset.section }));
      setSections((prev) => (JSON.stringify(prev) === JSON.stringify(liste) ? prev : liste));
    };
    lireRef.current = lire;
    setSections([]);
    setActif(null);
    restaure.current = null;
    lire();
    const obs = new MutationObserver(lire);
    obs.observe(main, { childList: true, subtree: true });
    return () => { obs.disconnect(); lireRef.current = null; };
  }, [pathname]);

  useEffect(() => {
    const garde = localStorage.getItem(cle(pathname));
    const s = sections.find((x) => stable(x.titre) === garde);
    if (s && restaure.current !== pathname) {
      restaure.current = pathname;
      setActif(s.id);
      clic.current = Date.now() + 3000;
      [300, 1200, 2500].forEach((ms) => setTimeout(() => { document.getElementById(s.id)?.scrollIntoView({ block: "start" }); }, ms));
    } else if (!garde || !s) {
      setActif((a) => (window.scrollY < 10 ? sections[0]?.id ?? null : a ?? sections[0]?.id ?? null));
    }
  }, [sections, pathname]);

  useEffect(() => {
    const suivre = () => {
      if (Date.now() < clic.current) { return; }
      const seuil = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--onglets-h")) || 0) + 100;
      const tous = sections.map((s) => ({ s, r: document.getElementById(s.id)?.getBoundingClientRect() })).filter((x) => x.r);
      if (window.scrollY > 0 && window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2) {
        // Bas de page : l'onglet choisi reste actif s'il est visible, sinon la dernière section visible
        const visibles = tous.filter((x) => x.r.top < window.innerHeight && x.r.bottom > seuil);
        if (visibles.length) { setActif((a) => (visibles.some((x) => x.s.id === a) ? a : visibles[visibles.length - 1].s.id)); return; }
      }
      const vus = tous.filter((x) => x.r.top <= Math.max(160, seuil));
      if (!vus.length) { setActif(sections[0]?.id ?? null); return; }
      const haut = Math.max(...vus.map((x) => x.r.top));
      const rangee = vus.filter((x) => Math.abs(x.r.top - haut) < 4).sort((a, b) => a.r.left - b.r.left);
      setActif(rangee[0].s.id);
    };
    window.addEventListener("scroll", suivre, { passive: true });
    return () => window.removeEventListener("scroll", suivre);
  }, [sections]);

  if (sections.length < 2) return null;
  const aller = (s) => {
    localStorage.setItem(cle(pathname), stable(s.titre));
    setActif(s.id);
    // Verrou jusqu'à la fin du défilement (scrollend), au plus 4 s
    clic.current = Date.now() + 4000;
    const fin = () => { clic.current = Date.now() + 150; window.removeEventListener("scrollend", fin); };
    window.addEventListener("scrollend", fin);
    document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const sauver = (o) => { enregistrerSections(pathname, o).catch((e) => toast.error(errMsg(e))); };
  const bouger = (s, j) => {
    const { blocs, blocDe } = blocsRef.current;
    const i = blocs.indexOf(blocDe[s.id]);
    if (i >= 0) { sauver(deplacer(blocs, i, j)); }
  };
  const rangDe = (s) => blocsRef.current.blocs.indexOf(blocsRef.current.blocDe[s.id]);
  return (
    <nav ref={nav} className="relative z-20 -mx-4 -mt-6 mb-6 border-b border-slate-200 bg-slate-100/90 px-4 py-2 backdrop-blur sm:-mx-8 sm:px-8 lg:sticky lg:top-0 lg:-mx-10 lg:-mt-10 lg:px-10" data-testid="onglets-sections">
      <div className="flex flex-wrap items-center gap-1">
        {sections.map((s) => {
          const on = actif === s.id;
          const r = rangDe(s);
          return (
            <span key={s.id} className={`flex max-w-full items-center ${org ? "rounded-lg border border-dashed border-slate-300 bg-white/50 pr-1" : ""}`}>
              <button type="button" onClick={() => aller(s)} data-testid={`onglet-${s.id}`}
                className={`flex max-w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs transition-colors sm:whitespace-nowrap sm:px-4 sm:text-sm ${on ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-600 hover:bg-white/60 hover:text-slate-900"}`}>
                <span className={`h-2 w-2 shrink-0 rounded-full ${on ? "bg-emerald-600" : "bg-slate-400"}`} />{court(s.titre)}
              </button>
              {org && r >= 0 && <OrdreBoutons i={r} n={blocsRef.current.blocs.length} onMove={(_, j) => bouger(s, j)} testId={`onglet-ordre-${s.id}`} clair horizontal />}
            </span>
          );
        })}
        <span className="ml-auto flex items-center gap-1">
          {org && ordre.length > 0 && (
            <button type="button" onClick={() => sauver([])} className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-slate-500 hover:bg-white/60 hover:text-slate-900" data-testid="onglets-reinitialiser">
              <RotateCcw className="h-3.5 w-3.5" />Ordre d'origine
            </button>
          )}
          <button type="button" onClick={() => setOrg(!org)} title="Organiser les sections de la page" data-testid="onglets-organiser"
            className={`flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs transition-colors ${org ? "bg-emerald-600 text-white" : "text-slate-500 hover:bg-white/60 hover:text-slate-900"}`}>
            <ArrowUpDown className="h-3.5 w-3.5" />{org ? "Terminer" : "Organiser"}
          </button>
        </span>
      </div>
    </nav>
  );
};
